import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, errText } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { Webhook, RefreshCw, Plus, Trash2, Send, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Loading, Banner, OPS_WEBHOOK_EVENTS, fmtDate } from '@/components/OpsUI';

export default function AppWebhooksPage() {
  const { t } = useTranslation();
  const [hooks, setHooks] = useState(null);
  const [deliveries, setDeliveries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ url: '', events: [...OPS_WEBHOOK_EVENTS] });
  const [secret, setSecret] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get('/ops/webhooks');
      setHooks(data);
    } catch (e) { setError(errText(e)); }
    try {
      const { data } = await api.get('/ops/webhooks/deliveries', { params: { limit: 30 } });
      setDeliveries(data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleEvent = (ev) => setForm((p) => ({
    ...p,
    events: p.events.includes(ev) ? p.events.filter((x) => x !== ev) : [...p.events, ev],
  }));

  const create = async () => {
    if (!form.url.trim() || form.events.length === 0) return;
    setSaving(true);
    try {
      const { data } = await api.post('/ops/webhooks', { url: form.url.trim(), events: form.events });
      if (data.secret) setSecret(data.secret);
      toast.success('Webhook creado');
      setForm({ url: '', events: [...OPS_WEBHOOK_EVENTS] });
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    setSaving(true);
    try { await api.delete(`/ops/webhooks/${id}`); toast.success('Webhook eliminado'); load(); }
    catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  const test = async (id) => {
    try { const { data } = await api.post(`/ops/webhooks/${id}/test`); toast.success(`Test: ${data.dispatched_event || 'ok'}`); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-6" data-testid="app-webhooks-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <Webhook className="h-6 w-6 text-[hsl(var(--primary))]" /> {t('nav_app_webhooks')}
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Eventos push de Tu Iglesia App → BOM</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="app-webhooks-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> {t('refresh')}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="ops-new-webhook-btn"><Plus className="h-3.5 w-3.5 mr-1" /> {t('ops_new_webhook')}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t('ops_new_webhook')}</DialogTitle><DialogDescription>Registra una URL del BOM para recibir eventos push de la App.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div><Label>{t('ops_webhook_url')}</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://bom.tudominio.com/ops-hook" data-testid="ops-webhook-url-input" /></div>
                <div>
                  <Label>{t('ops_webhook_events')}</Label>
                  <div className="mt-2 space-y-2">
                    {OPS_WEBHOOK_EVENTS.map((ev) => (
                      <label key={ev} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={form.events.includes(ev)} onCheckedChange={() => toggleEvent(ev)} data-testid={`ops-webhook-event-${ev}`} />
                        <span className="font-mono text-xs">{ev}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button disabled={saving || !form.url.trim() || form.events.length === 0} onClick={create} data-testid="ops-webhook-create-btn">{t('create')}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {secret && (
        <Banner kind="warn" title={t('ops_secret_once')} message={
          <span className="flex items-center gap-2 font-mono break-all">{secret}
            <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard?.writeText(secret); toast.success('Copiado'); }}><Copy className="h-3.5 w-3.5" /></Button>
          </span>
        } />
      )}

      {error && <Banner kind="error" message={String(error)} />}

      {loading ? <Loading label={t('loading')} /> : (
        <>
          <Card className="bg-card border border-border">
            <div className="p-4 border-b border-border text-sm font-display font-semibold">{t('ops_webhooks_title')} ({hooks?.count ?? 0})</div>
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border">
                    <th className="px-4 py-3">URL</th><th className="px-4 py-3">{t('ops_webhook_events')}</th><th className="px-4 py-3">active</th><th className="px-4 py-3 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {(hooks?.items || []).map((h) => (
                    <tr key={h.id} className="border-b border-border/40 hover:bg-accent/40" data-testid={`ops-webhook-row-${h.id}`}>
                      <td className="px-4 py-3 font-mono text-xs truncate max-w-[240px]">{h.url}</td>
                      <td className="px-4 py-3 text-xs">{(h.events || []).join(', ')}</td>
                      <td className="px-4 py-3">{h.active ? '✓' : '—'}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => test(h.id)} data-testid={`ops-webhook-test-${h.id}`}><Send className="h-3.5 w-3.5 mr-1" /> {t('ops_test')}</Button>
                        <Button variant="ghost" size="sm" disabled={saving} onClick={() => remove(h.id)} data-testid={`ops-webhook-delete-${h.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                  {(!hooks?.items || hooks.items.length === 0) && (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))]">{t('no_data')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="bg-card border border-border">
            <div className="p-4 border-b border-border text-sm font-display font-semibold">{t('ops_deliveries')}</div>
            <div className="max-h-[320px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-card">
                  <tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border">
                    <th className="px-3 py-2">event</th><th className="px-3 py-2">status</th><th className="px-3 py-2">attempts</th><th className="px-3 py-2">code</th><th className="px-3 py-2">when</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {(deliveries?.items || []).map((d) => (
                    <tr key={d.id} className="border-b border-border/40 hover:bg-accent/40">
                      <td className="px-3 py-2">{d.event}</td>
                      <td className="px-3 py-2">{d.status}</td>
                      <td className="px-3 py-2">{d.attempts}</td>
                      <td className="px-3 py-2">{d.last_status_code ?? '—'}</td>
                      <td className="px-3 py-2 text-[hsl(var(--muted-foreground))]">{fmtDate(d.created_at)}</td>
                    </tr>
                  ))}
                  {(!deliveries?.items || deliveries.items.length === 0) && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))] font-sans">{t('no_data')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
